import React, { useEffect, useState } from "react";

import { flatMap } from "lodash-es";
import { useTranslation } from "react-i18next";

import AdminAuth from "@/components/admin/AdminAuth";
import AdminHeader from "@/components/admin/AdminHeader";
import AdminSurveyResult from "@/components/admin/AdminSurveyResult";
import Button from "@/components/Button";
import Loader from "@/components/Loader";
import { useAdminStore } from "@/stores/adminStore";
import { prepareHeaders } from "@/utils";
import cincolabdataEndpoints from "@/utils/cincolabdata.endpoints";
import { getEvents } from "@/utils/events";
import { getFormInstance } from "@/utils/form";
import { getUserByEmail } from "@/utils/search";

import styles from "./administrator.module.css";

import type { Event } from "@/utils/events";
import type { PublicFormInstance } from "@/utils/form";
import type { Answer } from "@/utils/search";

const Admin = () => {
  const { t, i18n } = useTranslation();
  const adminStore = useAdminStore();

  const [email, setEmail] = useState("");
  const [instance, setInstance] = useState("");
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [forms, setForms] = useState<PublicFormInstance[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const verifySessionToken = async () => {
    const response = await fetch(cincolabdataEndpoints.auth.session, {
      method: "GET",
      headers: {
        ...prepareHeaders(),
        "x-cinco-token": adminStore.adminToken || "",
      },
      mode: "cors",
      redirect: "follow",
    });
    const data = await response.json();

    if (!response.ok) {
      console.log("Could not verify session token:", data.error);
      adminStore.setAdminToken(null);
      adminStore.setAdminLoggedIn(false);
      return;
    }

    adminStore.setAdminLoggedIn(true);
    adminStore.setAdminToken(data.token);
    adminStore.setAdminUser(data.user);
  };

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    setSearched(false);

    try {
      const allFormAnswers = await Promise.all([
        getUserByEmail(email, instance),
        getUserByEmail(email, instance.replace("DIGITAL_ACTIVATION", "SURVEY")),
      ]);
      console.log("allFormAnswers", allFormAnswers);
      setAnswers(flatMap(allFormAnswers, (form) => form.answers));
    } catch (err: unknown) {
      console.log("Error fetching user by email:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred",
      );
    } finally {
      setIsLoading(false);
      setSearched(true);
    }
  };

  const onReset = () => {
    setEmail("");
    setError("");
    setIsLoading(false);
    setInstance("");
    setAnswers([]);
    adminStore.reset();
  };

  useEffect(() => {
    if (adminStore.adminLoggedIn && !adminStore.adminToken) {
      adminStore.setAdminToken(null);
      adminStore.setAdminLoggedIn(false);
    }
    if (adminStore.adminToken) {
      verifySessionToken();
    }

    const loadEvents = async () => {
      const results = await getEvents();
      setEvents(results);
    };

    loadEvents();
  }, []);

  useEffect(() => {
    const loadForms = async () => {
      if (!instance) return;

      setForms([]);
      setFormsLoading(true);
      setError("");

      const [digitalActivationForm, surveyForm] = await Promise.all([
        getFormInstance(instance),
        getFormInstance(instance.replace("DIGITAL_ACTIVATION", "SURVEY")),
      ]);
      setFormsLoading(false);

      if (!digitalActivationForm || !surveyForm) {
        setError("Form not found");
        return;
      }
      setForms([digitalActivationForm, surveyForm]);
      setSearched(false);
    };

    loadForms();
  }, [instance]);

  return (
    <main className="grow flex flex-col">
      <img src="/img/homepage_cover_en.jpg" alt="" />
      {!adminStore.adminLoggedIn ? (
        <AdminAuth />
      ) : (
        <>
          <form
            className="grow flex flex-col py-4 px-5 items-center"
            onSubmit={onFormSubmit}
          >
            <AdminHeader
              setError={(newError: string) => setError(newError)}
              onAfterLogout={onReset}
            />

            <p className="text-center">Search for a user by email</p>
            <select
              value={instance}
              onChange={(e) => setInstance(e.target.value)}
              className="border-4 border-dark-blue rounded-full text-dark-blue mt-2 py-4 px-8 max-w-[400px] w-full"
              disabled={!events.length}
              required
            >
              <option value="" disabled>
                Select an event
              </option>
              {events.map((event: Event) => (
                <option key={event.instance_key} value={event.instance_key}>
                  {event.name[i18n.language as keyof typeof event.name]}
                </option>
              ))}
            </select>

            <div className="flex justify-center relative w-full max-w-[400px]">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                className={`border-4 border-dark-blue rounded-full text-dark-blue mt-2 py-4 px-8 max-w-[400px] w-full ${
                  !forms.length ? "opacity-50" : ""
                }`}
                placeholder="Email"
                disabled={!forms.length}
                required
              />
              {formsLoading && (
                <Loader className="absolute right-[25px] top-[calc(50%-5px)]" />
              )}
            </div>
            <Button className="mt-4" type="submit" loading={isLoading}>
              Search
            </Button>
            {error && <p className="mt-4">{error}</p>}
            {isLoading && (
              <p className={`mt-4 ${styles.loading}`}>Loading...</p>
            )}
          </form>

          {searched && forms.length && (
            <div className="px-5">
              {answers.length ? (
                <AdminSurveyResult answers={answers} forms={forms} />
              ) : (
                <span>
                  <p className="text-center mt-4">{t("admin.noResults")}</p>
                </span>
              )}
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default Admin;
